/**
 * Pose metrics screen. Everything the camera can honestly see, on one panel:
 * the live readings, the calibration routine, the fatigue drift, and the box
 * where a user says which body on screen is which player.
 *
 * All of the maths lives in ../scoring.js, which is pure and testable
 * (scripts/test-pose.mjs). This file is the part that touches the DOM.
 *
 * Three rules run through every line of it.
 *
 * 1. MEASURED means our code worked it out from body landmarks. LOGGED means
 *    a person tapped it. Every number on this panel is measured, every one of
 *    them is tagged, and nothing here ever counts a basket, a rebound or an
 *    assist. Those are taps, and they live in the game screen.
 * 2. An uncalibrated score is a rough guide and says so on screen. The
 *    starting thresholds are guesses that have never been checked against a
 *    real player. Calibration replaces them with the player's own range.
 * 3. The app cannot tell WHO a body belongs to. There is no face matching and
 *    no identity of any kind. Bodies get numbered boxes and the user names
 *    them, and when two players cross, the panel says the boxes may have
 *    swapped instead of pretending it knows.
 *
 * Body language, facial expression and eye movement are deliberately absent.
 * A camera at court distance cannot resolve them, and guessing at how a young
 * player feels from a webcam is not something this app does. When a session
 * needs to know how it felt, it asks, and that answer is self reported.
 */

import {
  TUNABLES, createCrew, calibratedTunables, buildBaseline, baselineStatus,
  CALIBRATION_STEPS, summarizeReps, frameQuality,
} from '../scoring.js';

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// v2 since 2026-09-24: knee and hip angles come from world points when the
// camera gives them, and a v1 baseline was measured in the picture. Applying
// one to the other shifted stance and first step scores without telling
// anyone, so a v1 baseline is left behind and the player calibrates again.
const BASELINE_KEY = 'hb.pose.baseline.v2';
const LABELS_KEY = 'hb.pose.labels.v1';
const PAINT_MS = 250;            // the live panel repaints four times a second, not thirty
const KEEP_REPS = 8;             // reps kept per body for the live list

const TABS = [['live', 'Live'], ['calibrate', 'Calibration'], ['fatigue', 'Fatigue'], ['who', 'Who is who']];

/**
 * Every line here is a promise about what the camera can and cannot do, and
 * every one of them is still true. What changed is the reading level: they were
 * written for an adult in long sentences, on a screen used by eight year olds
 * mid workout. Same facts, third grade words, short sentences. If you edit one,
 * keep the fact. The honesty is the point; the vocabulary is not.
 */
const COPY = {
  measured: 'I can see your body. I cannot see the ball. So you tap Made or Missed.',
  rough: 'These scores are a rough guess for now. Do the quick setup and they fit you.',
  part: 'Some of this is still a rough guess. Set up so far:',
  identity: 'I see bodies, not faces. I do not know who is who. Tap a box and type the name. If players cross, check the names.',
  crowded: 'Two players are very close. The boxes may have swapped. Check the names.',
  resumed: 'Someone left the picture and came back. I kept the same name. Wrong person? Clear the name.',
  resumedKit: 'Someone came back wearing the same colours, so I kept their name. I go by the shirt, not the face. Wrong person? Clear the name.',
  dribble: 'I watch your hand, not the ball. An empty hand going up and down counts too.',
  shot: 'I watch your arm, not the ball. I cannot see if it went in. You tap that.',
  boxout: 'I only see how you stand. I cannot see contact or the ball.',
  fatigue: 'Your last few reps next to your first few. It shows if your form slipped as you got tired. It is not a health check and it is not advice.',
  feelings: 'I do not read faces or moods. I will not guess how you feel. The journal asks you instead.',
};

const QUALITY = {
  nobody: 'Nobody in frame yet.',
  torso: 'Cannot see enough of the body. Step into frame.',
  far: 'Too far away to read knees and feet. Step in toward the phone, or move the phone out to you.',
  feet: 'Feet are out of frame. Stance, balance, box out and first step need them.',
  arms: 'Arms are out of frame. Shooting and dribbling need them.',
  partial: 'Only part of the body is visible. Scores will be patchy.',
};

const KINDS = {
  shot: { label: 'Shot motion', note: COPY.shot },
  dribble: { label: 'Dribble', note: COPY.dribble },
  balance: { label: 'Balance', note: null },
  stance: { label: 'Stance', note: null },
  boxout: { label: 'Box out', note: COPY.boxout },
  drive: { label: 'First step', note: null },
  pivot: { label: 'Pivot', note: 'I watch your feet in the picture. I cannot call a travel. A ref does that.' },
  slide: { label: 'Slide', note: 'I see you slide across the picture. Straight at the phone I cannot see it.' },
  footwork: { label: 'Footwork', note: 'I count feet going up and down. Steps straight at the phone are a rough count.' },
};

/* What each calibrated part actually buys, in the words the band uses. */
const PART_LABEL = {
  release: 'release angle',
  follow: 'follow through',
  dribble: 'dribble pace',
  stance: 'stance and box out depth',
  drive: 'first step and jump height',
};

const SIGNAL_LABEL = {
  releaseHeight: 'Release height',
  stanceDepth: 'Stance depth',
  jump: 'Jump height',
  restGapMs: 'Rest between reps',
};

const n1 = (v) => (Number.isFinite(v) ? Math.round(v * 10) / 10 : null);
const pct = (v) => `${Math.round(v)}%`;
const secs = (ms) => `${(ms / 1000).toFixed(1)}s`;

/* ------------------------------------------------------------------ state */

let ctx = null, root = null, panel = null, banner = null, tabsEl = null, mounted = false;
const listeners = new Set();

const S = {
  tab: 'live',
  crew: null,
  tun: TUNABLES,
  baseline: null,
  labels: {},
  bodies: new Map(),        // slot -> { seenAt, conf, live, reps: [] }
  crowded: false,
  quality: null,
  lastFrameAt: 0,
  resumedAt: -Infinity,
  running: false,
  calib: { on: false, reps: [], startedAt: 0, lastShot: null },
  paintedAt: 0,
  dirty: false,
};

/* Browser storage is a convenience, never a requirement. Private windows and
 * blocked site data both throw, and the panel has to work anyway. */
function readStore(key) {
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : null; }
  catch (e) { return null; }
}
function writeStore(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); return true; }
  catch (e) { return false; }
}

function applyBaseline(baseline) {
  S.baseline = baseline;
  S.tun = calibratedTunables(TUNABLES, baseline);
  S.crew?.setTunables(S.tun);
}

/* ------------------------------------------------------------------- boot */

/** The panel prefers #poseRoot. Until app.html has one, it makes its own. */
function findRoot() {
  const owned = document.getElementById('poseRoot');
  if (owned) return owned;
  const host = document.querySelector('#v-train aside') || document.querySelector('#v-train .train-grid');
  if (!host) return null;
  const card = document.createElement('div');
  card.id = 'poseRoot';
  host.appendChild(card);
  return card;
}

export function mount(c) {
  ctx = c || null;
  // Measuring comes first and does not depend on a panel existing. A game
  // screen mounts this module to get the reps, and a build where the training
  // sidebar is not on the page must still measure rather than go silent.
  if (!S.crew) {
    S.crew = createCrew(TUNABLES);
    applyBaseline(readStore(BASELINE_KEY));
    S.labels = readStore(LABELS_KEY) || {};
  }
  // The panel's styles are in /dist/app.css with the rest (scripts/build.mjs),
  // so nothing is fetched here: this used to add /css/posemetrics.css, a
  // request on every load for rules the page already had.
  root = findRoot();
  if (!root) return;

  root.innerHTML = `
    <div class="pm">
      <div class="pmtabs">${TABS.map(([id, label]) =>
    `<button type="button" data-ptab="${id}">${esc(label)}</button>`).join('')}</div>
      <div class="pmband" id="pmBand"></div>
      <div id="pmPanel"></div>
      <div class="pmsee" role="note" aria-label="What the camera can and cannot see">
        <div class="pmsee-col yes">
          <b><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z"/><circle cx="12" cy="12" r="3"/></svg>I can see</b>
          <span>Your body</span><span>Your arms</span><span>How you stand</span>
        </div>
        <div class="pmsee-col no">
          <b><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2.5 12S6 5.5 12 5.5c1.6 0 3 .4 4.3 1M21.5 12S18 18.5 12 18.5c-1.6 0-3-.4-4.3-1"/><path d="M4 4l16 16"/></svg>I cannot see</b>
          <span>The ball</span><span>The hoop</span><span>Your face or mood</span>
        </div>
      </div>
      <p class="pmfoot">${esc(COPY.measured)}</p>
    </div>`;
  panel = root.querySelector('#pmPanel');
  banner = root.querySelector('#pmBand');
  tabsEl = root.querySelector('.pmtabs');

  tabsEl.addEventListener('click', (e) => {
    const b = e.target.closest('button[data-ptab]');
    if (!b) return;
    S.tab = b.dataset.ptab;
    paintTabs(); paint();
  });
  root.addEventListener('click', onClick);
  root.addEventListener('submit', onSubmit);
  mounted = true;
  // The band carries the "this is a rough guide" line, so it has to be drawn
  // with the panel and not on the first camera frame. A player reading a score
  // before the camera starts still has to be told what it is standing on.
  paintTabs(); paintBand(); paint();
}

export function render() {
  if (!mounted) return;
  paintTabs(); paintBand(); paint();
}

function paintTabs() {
  if (!tabsEl) return;
  tabsEl.querySelectorAll('button[data-ptab]').forEach((b) => b.classList.toggle('on', b.dataset.ptab === S.tab));
}

/* ------------------------------------------------------- the frame stream */

/**
 * The one hook the camera loop calls. `poses` is what MediaPipe returned
 * (result.landmarks), either several bodies or one.
 *
 * `t` is a clock in milliseconds, and whatever clock goes in is the clock
 * every rep comes back on. For a recording that will be played back with the
 * feedback running alongside it, pass milliseconds from the start of the
 * recording, and rep.at is then already the position in the video.
 */
/** MediaPipe hands back several bodies or one. Either way, the first one. */
function firstPose(poses) {
  if (!Array.isArray(poses) || !poses.length) return null;
  return Array.isArray(poses[0]) ? poses[0] : poses;
}

/** What a box is called: the name somebody gave it, or "Body 3". For the stage to draw. */
export function nameOf(slot) { return labelFor(slot); }
/** Name a body from outside the panel: team mode does it from a jersey number. */
export function label(slot, name) {
  const key = String(slot);
  if (name) S.labels[key] = String(name).slice(0, 40); else delete S.labels[key];
  try { writeStore(LABELS_KEY, S.labels); paintBand(); } catch {}
}

/**
 * `opts.looks` is what each body is wearing, from public/js/looks.js. It is
 * optional: without it the tracker runs on movement alone.
 */
export function onFrame(poses, t, opts = {}) {
  if (!S.crew) return null;
  let result = null;
  try { result = S.crew.push(poses, t, opts); }
  catch (e) { console.error('pose metrics frame', e); return null; }
  S.lastFrameAt = t;
  S.crowded = result.crowded;

  for (const p of result.people) {
    const body = S.bodies.get(p.slot) || { reps: [], seenAt: 0, conf: 0, live: null };
    body.seenAt = t;
    body.conf = p.conf;
    body.live = S.crew.scorer(p.slot).live;
    S.bodies.set(p.slot, body);
    if (p.resumed) { S.resumedAt = t; S.resumedBy = p.resumedBy || 'alone'; }
  }
  // A body that came and went without ever being measured is clutter, not a
  // player. Drop it from the panel rather than leaving an empty card behind.
  for (const [slot, body] of S.bodies) {
    if (!body.reps.length && t - body.seenAt > 60000) S.bodies.delete(slot);
  }
  // With nobody matched to a slot the panel used to say "nobody in frame",
  // which is wrong and unhelpful on a court: the usual reason is a body that
  // IS there with its torso or its feet cut off. Read the picture and say so.
  S.quality = result.people.length
    ? S.crew.scorer(result.people[0].slot).live.quality
    : frameQuality(firstPose(poses), S.tun);

  for (const rep of result.reps) {
    const body = S.bodies.get(rep.slot);
    if (body) {
      body.reps.push(rep);
      if (body.reps.length > KEEP_REPS) body.reps.shift();
    }
    if (S.calib.on) {
      S.calib.reps.push(rep);
      if (rep.kind === 'shot') S.calib.lastShot = rep;
      if (S.calib.reps.length > 800) S.calib.reps.shift();
    }
    emit(rep);
  }

  S.dirty = true;
  maybePaint(t);
  return result;
}

/** Repaint a few times a second, never once per frame. */
function maybePaint(t) {
  if (!mounted || !S.dirty) return;
  const now = typeof t === 'number' ? t : performance.now();
  if (now - S.paintedAt < PAINT_MS) return;
  S.paintedAt = now;
  S.dirty = false;
  paintBand();
  if (S.tab === 'live' || S.tab === 'calibrate' || S.tab === 'fatigue') paint();
}

/** Called when a session starts, so a run gets its own numbers. */
export function start() {
  S.running = true;
  S.bodies.clear();
  S.crew?.reset();
  S.crowded = false;
  S.resumedAt = -Infinity;
  render();
}

/** Called when a session ends. Anything still being held is counted. */
export function stop() {
  S.running = false;
  S.crowded = false;
  S.resumedAt = -Infinity;
  const held = S.crew?.flush() || [];
  for (const rep of held) {
    const body = S.bodies.get(rep.slot);
    if (body) { body.reps.push(rep); if (body.reps.length > KEEP_REPS) body.reps.shift(); }
    if (S.calib.on) S.calib.reps.push(rep);
    // A stance still being held at the buzzer counts in summary(), so it has
    // to reach the listeners too. Without this the run timeline and the run
    // card disagreed about how many reps there were.
    emit(rep);
  }
  render();
  return summary();
}

/** Hand one measured rep to every subscriber. One bad listener never stops the rest. */
function emit(rep) {
  const out = { ...rep, player: labelFor(rep.slot) };
  for (const fn of listeners) { try { fn(out); } catch (e) { console.error('pose rep listener', e); } }
}

/** Everything measured this session, per body, for a report or a run card. */
export function summary() {
  if (!S.crew) return { source: 'measured', bodies: {} };
  const bySlot = S.crew.summary();
  const bodies = {};
  for (const [slot, data] of Object.entries(bySlot)) {
    // Slots nothing was ever measured from are noise, not players.
    if (!data.total) continue;
    bodies[slot] = { ...data, player: labelFor(Number(slot)), assigned: Boolean(S.labels[slot]) };
  }
  return {
    source: 'measured',
    calibrated: Boolean(S.tun.CALIBRATED),
    calibration: S.tun.CALIBRATED || null,
    bodies,
    note: COPY.identity,
  };
}

/** Subscribe to measured reps, for the game screen. Returns an unsubscribe. */
export function onRep(fn) {
  if (typeof fn !== 'function') return () => {};
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function baseline() { return S.baseline; }

/** The last shot this panel saw, marked made or missed by a person. */
export function markLastShot(made) {
  if (!S.calib.lastShot) return false;
  S.calib.lastShot.made = made === true;
  S.dirty = true;
  paint();
  return true;
}

function labelFor(slot) {
  const named = S.labels[String(slot)];
  return named ? String(named) : `Body ${slot}`;
}

/* ------------------------------------------------------------ the banner */

function paintBand() {
  if (!banner) return;
  const bits = [];
  const q = S.quality;
  if (q && !q.ok) {
    const reason = (q.reasons || []).find((r) => QUALITY[r]);
    if (reason) bits.push(`<div class="pmnote warn">${esc(QUALITY[reason])}</div>`);
  }
  if (S.crowded) bits.push(`<div class="pmnote warn">${esc(COPY.crowded)}</div>`);
  if (S.lastFrameAt - S.resumedAt < 8000) bits.push(`<div class="pmnote">${esc(S.resumedBy === 'clothing' ? COPY.resumedKit : COPY.resumed)}</div>`);
  bits.push(`<div class="pmnote">${esc(calibrationLine())}</div>`);
  banner.innerHTML = bits.join('');
}

/**
 * One line saying how much of what is on screen is real. Calibrating one part
 * used to clear the warning for all five, which read as though every score had
 * been checked against the player. It says what was checked and what was not.
 */
function calibrationLine() {
  const parts = S.tun.CALIBRATED?.parts;
  if (!parts) return COPY.rough;
  const done = Object.keys(PART_LABEL).filter((k) => parts[k]).map((k) => PART_LABEL[k]);
  const left = Object.keys(PART_LABEL).filter((k) => !parts[k]).map((k) => PART_LABEL[k]);
  if (!left.length) return `Measured against you: ${listOf(done)}. Elbow under the ball stays fixed, because that one is geometry.`;
  return `${COPY.part} ${listOf(done)}. Still a rough guide: ${listOf(left)}.`;
}

/** "a, b and c", so a list reads like a sentence. */
function listOf(items) {
  if (!items.length) return 'nothing yet';
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/* -------------------------------------------------------------- painting */

function paint() {
  if (!panel) return;
  try {
    if (S.tab === 'live') panel.innerHTML = liveView();
    else if (S.tab === 'calibrate') panel.innerHTML = calibrateView();
    else if (S.tab === 'fatigue') panel.innerHTML = fatigueView();
    else panel.innerHTML = whoView();
  } catch (e) {
    console.error('pose metrics paint', e);
    panel.innerHTML = `<div class="card"><p class="pmdim">Could not draw this panel. ${esc(e.message)}</p></div>`;
  }
}

const tag = (kind = 'measured') => `<span class="pmtag ${kind}">${kind === 'measured' ? 'Measured' : 'Logged'}</span>`;

function meter(label, value, extra = '') {
  const v = Number.isFinite(value) ? Math.round(value) : null;
  return `
    <div class="pmmeter">
      <div class="pmmeter-h"><span>${esc(label)}</span><b>${v === null ? '&ndash;' : v}</b></div>
      <div class="bar"><i style="width:${v === null ? 0 : v}%"></i></div>
      ${extra ? `<small>${esc(extra)}</small>` : ''}
    </div>`;
}

/** One line of the raw numbers behind a score, so the panel explains itself. */
function metricLine(rep) {
  const m = rep.metrics || {};
  if (rep.kind === 'dribble') {
    const depth = Math.abs(Math.round(m.lowHeight * 100));
    return [
      m.cadenceHz ? `${m.cadenceHz} a second` : null,
      `hand ${depth} percent of a torso ${m.lowHeight < 0 ? 'below' : 'above'} the hip at the bounce`,
      m.alternated ? 'changed hands' : null,
    ].filter(Boolean).join(', ');
  }
  if (rep.kind === 'balance') {
    return `weight ${Math.round(Math.abs(m.offset) * 100)} percent of the way out to one foot, wobble ${n1(m.sway * 100) ?? 0}, lean ${m.lean} degrees`;
  }
  if (rep.kind === 'stance') {
    return `held ${secs(m.holdMs)}, knees ${m.kneeMean} degrees, feet ${m.widthRatio} times shoulder width, hands up ${pct(m.handsUpPct)} of it`;
  }
  if (rep.kind === 'boxout') {
    return `held ${secs(m.holdMs)}, base ${m.widthRatio} times shoulder width, arms ${m.armSpread}, knees ${m.kneeMean} degrees`;
  }
  if (rep.kind === 'drive') {
    return [
      m.stepFoot ? `first step off the ${m.stepFoot} foot` : 'first step',
      m.firstStepMs ? `${m.firstStepMs}ms to move` : null,
      `${Math.round(m.firstStepLen * 100)} percent of a torso of travel`,
      m.kneePush ? `pushed from ${m.kneePush} degrees` : null,
    ].filter(Boolean).join(', ');
  }
  if (rep.kind === 'shot') {
    // Held time is in ms now; a rep saved before that carries frames at 30 fps.
    return `release ${m.releaseAngle} degrees, elbow ${m.elbowOffset}, held ${secs(Number.isFinite(m.followMs) ? m.followMs : (m.followFrames || 0) * (1000 / 30))}`;
  }
  const times = (n) => `${n} time${n === 1 ? '' : 's'}`;
  if (rep.kind === 'pivot') {
    return [
      `${m.plantFoot} foot planted ${secs(m.holdMs)}`,
      // A rep saved before 2026-09-24 says lifted, and measured the swing in
      // the picture rather than in torso lengths.
      (m.plantMoved ?? m.lifted) ? 'then it moved' : 'and it stayed put',
      `free foot swung ${Math.round(m.freeTravel * 100)} percent of ${'plantMoved' in m ? 'a torso' : 'the picture'}`,
      m.hipTurnDeg ? `shoulders turned ${m.hipTurnDeg} degrees` : null,
      Number.isFinite(m.confidence) && m.confidence < 0.8 ? `plant foot ${Math.round(m.confidence * 100)} percent sure` : null,
    ].filter(Boolean).join(', ');
  }
  if (rep.kind === 'slide') {
    return [
      `${secs(m.durationMs)} sliding`,
      `${Math.round(m.distance * 100)} percent of the picture covered`,
      `knees ${m.kneeMean} degrees`,
      `feet ${m.widthRatio} times shoulder width`,
      m.crossings ? `feet crossed ${times(m.crossings)}` : 'feet never crossed',
      m.directionChanges ? `changed direction ${times(m.directionChanges)}` : null,
    ].filter(Boolean).join(', ');
  }
  if (rep.kind === 'footwork') {
    return [
      `${m.steps} steps`,
      m.stepsPerSec ? `${m.stepsPerSec} a second` : null,
      `about ${Math.round(m.avgStepLen * 100)} percent of the picture each`,
      m.directionChanges ? `changed direction ${times(m.directionChanges)}` : null,
    ].filter(Boolean).join(', ');
  }
  return '';
}

function repRow(rep) {
  const kind = KINDS[rep.kind] || { label: rep.kind };
  return `
    <div class="pmrep">
      <b>${esc(kind.label)}</b>
      <span class="pmscore">${Number.isFinite(rep.overall) ? rep.overall : '&ndash;'}</span>
      <small>${esc(metricLine(rep))}</small>
    </div>`;
}

function liveView() {
  const bodies = [...S.bodies.entries()].sort((a, b) => a[0] - b[0]);
  if (!bodies.length) {
    return `<div class="card"><h3>Live</h3><p class="pmdim">I cannot see you yet. Start the camera. Step back until I can see your feet.</p></div>`;
  }
  return bodies.map(([slot, body]) => {
    const live = body.live || {};
    const stale = S.lastFrameAt - body.seenAt > 1500;
    const reps = [...body.reps].reverse();
    const counts = summarizeReps(S.crew.scorer(slot).reps).counts;
    const doing = live.inBoxout ? 'boxing out' : live.inStance ? 'in a stance' : live.driving ? 'driving' : 'moving';
    return `
      <div class="card pmbody${stale ? ' stale' : ''}">
        <div class="pmhead">
          <b>${esc(labelFor(slot))}</b>
          ${tag('measured')}
          <span class="pmdim">${stale ? 'out of frame' : esc(doing)}</span>
        </div>
        <div class="pmstats grid-stats">
          <div class="stat"><b>${counts.shot || 0}</b><small>shot motions</small></div>
          <div class="stat"><b>${counts.dribble || 0}</b><small>dribbles</small></div>
          <div class="stat"><b>${counts.stance || 0}</b><small>stances</small></div>
          <div class="stat"><b>${counts.drive || 0}</b><small>first steps</small></div>
          <div class="stat"><b>${counts.boxout || 0}</b><small>box out shapes</small></div>
          <div class="stat"><b>${Number.isFinite(live.jumpRise) ? n1(live.jumpRise * 100) : '&ndash;'}</b><small>hip rise now</small></div>
        </div>
        ${reps.length ? `<div class="pmreps">${reps.map(repRow).join('')}</div>`
    : '<p class="pmdim">No reps yet for this body.</p>'}
        ${reps.some((r) => KINDS[r.kind]?.note)
    ? `<p class="pmdim small">${esc([...new Set(reps.map((r) => KINDS[r.kind]?.note).filter(Boolean))].join(' '))}</p>` : ''}
      </div>`;
  }).join('');
}

/* ----------------------------------------------------------- calibration */

function calibCounts() {
  const counts = summarizeReps(S.calib.reps).counts;
  const made = S.calib.reps.filter((r) => r.kind === 'shot' && r.made === true).length;
  return { ...counts, made };
}

function calibrateView() {
  const status = baselineStatus(S.baseline, { T: TUNABLES });
  const counts = calibCounts();
  const parts = status.parts || {};

  const steps = CALIBRATION_STEPS.map((step) => {
    const have = counts[step.kind] || 0;
    const done = have >= step.need;
    return `
      <div class="pmstep${done ? ' done' : ''}">
        <div class="pmstep-h"><b>${esc(step.title)}</b><span>${have} of ${step.need}</span></div>
        <div class="bar"><i style="width:${Math.min(100, (have / step.need) * 100)}%"></i></div>
        <small>${esc(step.what)}</small>
        <small class="pmdim">${esc(step.gives)}</small>
      </div>`;
  }).join('');

  const shotCount = counts.shot || 0;
  const shotBlock = S.calib.on && shotCount > 0 ? `
    <div class="pmmark">
      <span>${counts.made} of ${shotCount} marked made. The release target only becomes yours once ${TUNABLES.CALIBRATION.MADE_NEEDED} are marked.</span>
      <div class="pmmark-b">
        <button class="btn" type="button" data-pm="made"${S.calib.lastShot ? '' : ' disabled'}>Last one made</button>
        <button class="btn" type="button" data-pm="missed"${S.calib.lastShot ? '' : ' disabled'}>Missed</button>
      </div>
    </div>` : '';

  const saved = status.calibrated ? `
    <div class="card">
      <h3>Your baseline</h3>
      <p class="pmdim">Recorded ${status.ageDays === 0 ? 'today' : `${status.ageDays} days ago`}${status.stale ? '. Old enough that it is worth doing again.' : '.'}</p>
      <ul class="pmlist">
        <li>${parts.release ? tag('measured') : '<span class="pmtag guess">Still a guess</span>'} Release angle, from the shots you marked made</li>
        <li>${parts.follow ? tag('measured') : '<span class="pmtag guess">Still a guess</span>'} Follow through hold</li>
        <li>${parts.dribble ? tag('measured') : '<span class="pmtag guess">Still a guess</span>'} Dribble pace</li>
        <li>${parts.stance ? tag('measured') : '<span class="pmtag guess">Still a guess</span>'} Stance depth</li>
        <li>${parts.drive ? tag('measured') : '<span class="pmtag guess">Still a guess</span>'} First step and jump height</li>
        <li><span class="pmtag guess">Left alone</span> Elbow under the ball. That one is geometry, not taste, so calibration never moves it.</li>
      </ul>
      <button class="btn" type="button" data-pm="clear">Clear my baseline</button>
    </div>` : '';

  return `
    <div class="card">
      <h3>Calibration</h3>
      <p class="pmdim">Nothing in this app has been measured against a real player yet. A few reps of your own give every score something real to stand on. Whatever you skip stays marked as a guess.</p>
      <div class="pmsteps">${steps}</div>
      ${shotBlock}
      <div class="pmactions">
        <button class="btn${S.calib.on ? '' : ' primary'}" type="button" data-pm="${S.calib.on ? 'calib-stop' : 'calib-start'}">${S.calib.on ? 'Stop recording' : 'Start calibration'}</button>
        <button class="btn primary" type="button" data-pm="calib-save"${S.calib.reps.length ? '' : ' disabled'}>Save baseline</button>
        <button class="btn" type="button" data-pm="calib-reset"${S.calib.reps.length ? '' : ' disabled'}>Start over</button>
      </div>
      ${S.calib.on ? '<p class="pmdim">Recording. Everything the camera measures from here is going into your baseline.</p>' : ''}
    </div>
    ${saved}`;
}

/* --------------------------------------------------------------- fatigue */

function driftRow(key, signal, compare) {
  const label = SIGNAL_LABEL[key] || key;
  if (!signal.ready) {
    return `<div class="pmdrift"><div class="pmdrift-h"><b>${esc(label)}</b><span class="pmdim">${signal.n} of ${signal.need} reps</span></div></div>`;
  }
  const dir = signal.driftPct > 0 ? 'up' : signal.driftPct < 0 ? 'down' : 'flat';
  const size = Math.min(100, Math.abs(signal.driftPct) * 3);
  const word = signal.band === 'noise' ? 'about the same' : `${Math.abs(signal.driftPct)} percent ${dir}`;
  return `
    <div class="pmdrift${signal.tiring ? ' tiring' : ''}">
      <div class="pmdrift-h"><b>${esc(label)}</b><span>${esc(word)}</span></div>
      <div class="bar"><i class="${dir}" style="width:${size}%"></i></div>
      <small>first ${compare.baselineReps} reps ${signal.baseline}, last ${compare.recentReps} ${signal.recent}</small>
    </div>`;
}

function fatigueView() {
  const bodies = [...S.bodies.keys()].sort((a, b) => a - b);
  if (!bodies.length) return `<div class="card"><h3>Fatigue</h3><p>${esc(COPY.fatigue)}</p><p class="pmdim">Nothing recorded yet this session.</p></div>`;

  return bodies.map((slot) => {
    const report = S.crew.scorer(slot).fatigue();
    const rows = Object.entries(report.signals).map(([k, v]) => driftRow(k, v, report.compare)).join('');
    // With more than one body on the panel, "your own" was being said about
    // somebody else's reps. Name the body instead. The comparison is always
    // with that body's own opening reps, never with anybody else's.
    const whose = bodies.length > 1 ? `${labelFor(slot)}'s own` : 'your own';
    const head = report.ready && report.headline
      ? `<p class="pmhead-line">${esc(SIGNAL_LABEL[report.headline.key] || report.headline.key)} is ${Math.abs(report.headline.driftPct)} percent ${report.headline.driftPct < 0 ? 'lower' : 'higher'} than ${esc(whose)} first ${report.compare.baselineReps} reps today.</p>`
      : report.ready
        ? `<p class="pmhead-line">Nothing has drifted away from how ${esc(bodies.length > 1 ? labelFor(slot) : 'you')} started today.</p>`
        : `<p class="pmdim">Not enough yet. It needs ${report.need.reps} reps of the same thing and about ${Math.round(report.need.sessionMs / 60000)} minutes, so the comparison means something.</p>`;
    return `
      <div class="card">
        <div class="pmhead"><b>${esc(labelFor(slot))}</b>${tag('measured')}</div>
        ${head}
        <div class="pmdrifts">${rows}</div>
        <p class="pmdim">${esc(COPY.fatigue)}</p>
      </div>`;
  }).join('');
}

/* ----------------------------------------------------------- who is who */

function whoView() {
  const bodies = [...S.bodies.entries()].sort((a, b) => a[0] - b[0]);
  const rows = bodies.length ? bodies.map(([slot, body]) => `
    <form class="pmwho" data-slot="${slot}">
      <div class="pmwho-d">
        <b>Body ${slot}</b>
        <small class="pmdim">${S.lastFrameAt - body.seenAt > 1500 ? 'out of frame' : 'in frame'}, ${Math.round((body.conf || 0) * 100)} percent of joints clear</small>
      </div>
      <input type="text" name="label" maxlength="40" value="${esc(S.labels[String(slot)] || '')}" placeholder="Who is this?" aria-label="Name for body ${slot}">
      <button class="btn" type="submit">Save</button>
    </form>`).join('') : '<p class="pmdim">Nobody in frame yet.</p>';

  return `
    <div class="card">
      <h3>Who is who</h3>
      <p>${esc(COPY.identity)}</p>
      ${rows}
      <p class="pmdim">A name here is a label on this device. It does not link anybody to a Hooper Buddy account, and nothing measured here is written to another player's record from this panel.</p>
    </div>`;
}

/* ---------------------------------------------------------------- events */

function onSubmit(e) {
  const form = e.target.closest('form.pmwho');
  if (!form) return;
  e.preventDefault();
  const slot = form.dataset.slot;
  const value = String(new FormData(form).get('label') || '').trim().slice(0, 40);
  if (value) S.labels[slot] = value; else delete S.labels[slot];
  writeStore(LABELS_KEY, S.labels);
  ctx?.toast?.(value ? `Body ${slot} is ${value} on this device.` : `Name cleared for body ${slot}.`);
  paint();
}

async function onClick(e) {
  const b = e.target.closest('button[data-pm]');
  if (!b) return;
  const what = b.dataset.pm;

  if (what === 'calib-start') {
    S.calib = { on: true, reps: [], startedAt: Date.now(), lastShot: null };
    ctx?.toast?.('Calibrating. Do the four bits in your own time.');
    paint(); return;
  }
  if (what === 'calib-stop') { S.calib.on = false; paint(); return; }
  if (what === 'calib-reset') {
    S.calib.reps = []; S.calib.lastShot = null;
    paint(); return;
  }
  if (what === 'made' || what === 'missed') { markLastShot(what === 'made'); return; }

  if (what === 'calib-save') {
    const built = buildBaseline(S.calib.reps, { at: Date.now() });
    const status = baselineStatus(built, { T: TUNABLES });
    if (!status.calibrated) {
      ctx?.toast?.('Not enough reps yet for any part of it. Nothing saved.', true);
      return;
    }
    applyBaseline(built);
    const stored = writeStore(BASELINE_KEY, built);
    S.calib.on = false;
    ctx?.toast?.(stored
      ? 'Baseline saved. Your scores are measured against you now.'
      : 'Baseline is in use for this session. This browser would not store it, so it is gone when you close the tab.');
    paint(); paintBand(); return;
  }

  if (what === 'clear') {
    const yes = ctx?.ui?.confirm
      ? await ctx.ui.confirm({
        title: 'Clear your baseline?',
        body: 'Scores go back to the starting numbers, which are guesses that have never been checked against a real player.',
        confirmText: 'Clear it', danger: true,
      })
      : true;
    if (!yes) return;
    applyBaseline(null);
    try { localStorage.removeItem(BASELINE_KEY); } catch (err) { /* nothing to do */ }
    ctx?.toast?.('Baseline cleared.');
    paint(); paintBand();
  }
}
